"use client";

import { useEffect, useRef, useState } from "react";

// 716 Cannabis Dutchie store/location id (the embedded-menu id, NOT the Plus
// API retailer id). This system is independent of the Plus GraphQL API, so it
// keeps working even when Plus access is down.
const STORE_ID = "64c8f9e37f0cc30009df1a56";
const EMBED_SCRIPT_SRC = `https://dutchie.com/api/v2/embedded-menu/${STORE_ID}.js`;

/**
 * Mounts Dutchie's own embedded menu (browse + cart + checkout, all served
 * inside their iframe). Used as a lightweight, self-contained storefront when
 * our Plus-API-powered custom shop can't refresh. No product data of ours is
 * involved — Dutchie renders everything.
 */
export default function DutchieEmbedMenu() {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  // Disable scroll anchoring while this page is mounted. The collapsing header
  // sheds ~112px; with anchoring on, the browser adjusts scrollY to keep the
  // view stable, which flip-flops the collapse threshold and jitters. Off, the
  // collapse animates smoothly and scrollY stays put. Scoped to /menu only.
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.overflowAnchor;
    html.style.overflowAnchor = "none";
    return () => {
      html.style.overflowAnchor = prev;
    };
  }, []);

  useEffect(() => {
    if (!holderRef.current) return;
    const holder = holderRef.current;

    // Avoid double-mounting on fast refresh / re-render.
    if (holder.querySelector("#dutchie--embed__script")) {
      setStatus("ready");
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.id = "dutchie--embed__script";
    script.src = EMBED_SCRIPT_SRC;
    script.onload = () => {
      // The embed injects its iframe asynchronously; poll briefly so we can
      // clear the loading state once it's actually on the page.
      let tries = 0;
      const iv = setInterval(() => {
        tries++;
        const hasIframe = !!holder.querySelector("iframe") || !!(window as any).DutchieEmbed;
        if (hasIframe) {
          clearInterval(iv);
          setStatus("ready");
        } else if (tries > 40) {
          clearInterval(iv);
          setStatus("ready"); // don't trap the user behind a spinner
        }
      }, 400);
    };
    script.onerror = () => setStatus("error");
    holder.appendChild(script);

    return () => {
      // Leave the script in place across route changes so it doesn't re-fetch;
      // the guard above prevents duplicates.
    };
  }, []);

  return (
    <div className="w-full">
      {status === "loading" && (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary_green" />
          <p className="text-sm text-gray-500">Loading the menu…</p>
        </div>
      )}
      {status === "error" && (
        <div className="max-w-lg mx-auto text-center py-20 px-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Menu is momentarily unavailable</h2>
          <p className="text-gray-600 mb-6">
            We&apos;re having trouble loading the live menu. Please refresh, or
            reach us directly and we&apos;ll help you place an order.
          </p>
          <a
            href="tel:+17165843123"
            className="inline-flex items-center gap-2 px-5 py-3 bg-primary_green text-white rounded-lg font-semibold hover:bg-primary_green/90"
          >
            Call (716) 584-3123
          </a>
        </div>
      )}
      {/* Dutchie renders its menu iframe inside this holder. */}
      <div ref={holderRef} className={status === "ready" ? "block" : "hidden"} />
    </div>
  );
}
